import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, ArrowUpDown, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from './ui/button';
import {
  DashboardSummary, DashboardSummaryBucket, DashboardBucket, DASHBOARD_BUCKETS,
  DashboardSummaryDays, DASHBOARD_SUMMARY_DAYS_OPTIONS,
} from '@/supabase/order/types/dashboardSummary';
import { buildKeepListView } from '@/utils/keepByCustomer';
import { downloadKeepListExcel } from '@/utils/excelUtils';
import { logError } from '@/lib/sentry';

const KEEP_MODE_OPTIONS = [
  { value: 'product', label: '상품별' },
  { value: 'customer', label: '고객별' },
] as const;

interface OrderStatusSummaryProps {
  summary: DashboardSummary | null;
  isLoading: boolean;
  error: Error | null;
  days: DashboardSummaryDays;
  /** 실제 로드 완료된 데이터의 기간 — stale 표시 중 라벨 불일치 방지 (null = 아직 로드 전) */
  loadedDays: DashboardSummaryDays | null;
  onDaysChange: (days: DashboardSummaryDays) => void;
  onRetry: () => void;
}

export default function OrderStatusSummary({ summary, isLoading, error, days, loadedDays, onDaysChange, onRetry }: OrderStatusSummaryProps) {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{ key: 'name' | 'totalQuantity'; direction: 'asc' | 'desc' }>({ key: 'totalQuantity', direction: 'desc' });
  // 기본값 '상품별' = 현행 유지(무회귀). 토글은 화면 확인용이고, 발견 수단은 다운로드 버튼이다(스펙 §3.2)
  const [keepMode, setKeepMode] = useState<'product' | 'customer'>('product');

  // 화면 고객별 보기와 엑셀 2시트가 공유하는 단일 소스 — 고정 규칙 정렬(스펙 §3.3)
  const keepListView = useMemo(() => buildKeepListView(summary?.keepItems ?? []), [summary]);

  const handleKeepExcelDownload = useCallback(async () => {
    try {
      await downloadKeepListExcel(keepListView, loadedDays ?? days);
    } catch (error) {
      // 에러 객체를 첫 인자로 — 문자열이 먼저 오면 Sentry가 captureMessage로 처리해 스택을 잃는다
      logError(error, '킵 포장목록 엑셀 다운로드 실패');
      toast.error('엑셀 파일 다운로드 중 오류가 발생했습니다.');
    }
  }, [keepListView, loadedDays, days]);

  const sortedKeepItems = useMemo(() => {
    if (!summary) return [];
    // state 소유 배열 직접 sort 금지 — 복사 후 정렬 (스펙 §2)
    const entries = [...summary.keepItems];
    entries.sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }
      if (a.totalQuantity === b.totalQuantity) return 0;
      return sortConfig.direction === 'asc' ? a.totalQuantity - b.totalQuantity : b.totalQuantity - a.totalQuantity;
    });
    return entries;
  }, [summary, sortConfig]);

  const handleNicknameClick = (date: string, nickname: string) => {
    navigate(`/order?date=${date}&nickname=${encodeURIComponent(nickname)}`);
  };

  const requestSort = (key: 'name' | 'totalQuantity') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const renderStatusColumn = (status: DashboardBucket, bucket: DashboardSummaryBucket) => {
    const statusConfig = {
      '미지정': { color: 'bg-gray-200', textColor: 'text-gray-800' },
      '부분 입금': { color: 'bg-amber-200', textColor: 'text-amber-800' },
      '입금 완료': { color: 'bg-yellow-200', textColor: 'text-yellow-800' },
      '출고 완료': { color: 'bg-green-200', textColor: 'text-green-800' },
      '킵': { color: 'bg-red-200', textColor: 'text-red-800' },
    };
    const omitted = bucket.count - bucket.orders.length;

    return (
      <div key={status} className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className={`font-semibold mb-3 p-2 rounded text-center ${statusConfig[status].color} ${statusConfig[status].textColor}`}>
          {status} <Badge variant="secondary">{bucket.count}</Badge>
        </h3>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {bucket.orders.length > 0 ? (
            <>
              {bucket.orders.map((order) => (
                <div
                  key={order.id}
                  className="text-sm p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                  onClick={() => handleNicknameClick(order.order_date, order.nickname)}
                >
                  <div className="flex justify-between items-center">
                    <span>{order.nickname}</span>
                    <span className="text-xs text-gray-500">{format(new Date(order.order_date), 'MM월 dd일')} 정산서</span>
                  </div>
                </div>
              ))}
              {omitted > 0 && (
                <p className="text-xs text-gray-400 text-center py-1">…외 {omitted.toLocaleString()}건 (오래된 순 생략)</p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">해당 상태의 주문이 없습니다.</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="space-y-1.5">
            <CardTitle>주문 현황 요약</CardTitle>
            <CardDescription>
              {/* 표시 중인 데이터의 실제 기간을 에코 — 토글 직후 stale 데이터가 새 기간으로 오표기되지 않게 */}
              최근 <span className="font-medium tabular-nums text-gray-700">{loadedDays ?? days}일</span> 주문 · 향후 30일 예약 포함
            </CardDescription>
          </div>
          {/* 본문 TabsList와 동일한 문법(gray-100 트랙 + 흰 필)의 축소판 — shadcn Button의 secondary/ghost는
              이 레포 tailwind.config에 시맨틱 토큰이 없어 죽은 클래스라 raw button 사용 */}
          <div
            role="group"
            aria-label="조회 기간 선택"
            className="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
          >
            {DASHBOARD_SUMMARY_DAYS_OPTIONS.map((option) => {
              const isActive = option === days;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={isActive}
                  disabled={isLoading && !isActive}
                  onClick={() => { if (!isActive) onDaysChange(option); }}
                  className={cn(
                    "inline-flex h-7 min-w-[3rem] items-center justify-center rounded-md px-2.5",
                    "text-xs font-medium tabular-nums transition-colors duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2",
                    "disabled:pointer-events-none disabled:opacity-50",
                    isActive
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-900",
                  )}
                >
                  {option}일
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent aria-busy={isLoading}>
        {!summary && !error ? (
          // 최초 로드에만 스피너 — 이후 기간 전환 refetch는 stale 콘텐츠 유지 + dim (레이아웃 점프 방지)
          <div className="flex min-h-[280px] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
        ) : error && !summary ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-red-500">요약을 불러오지 못했습니다: {error.message}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>다시 시도</Button>
          </div>
        ) : summary ? (
          <>
          {error && (
            // refetch 실패 — 이전 기간의 stale 데이터는 유지한 채 인라인으로 알림 (codexloop R1 #4)
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-sm text-red-600">요약을 불러오지 못했습니다. 이전 데이터가 표시 중입니다.</p>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onRetry}>다시 시도</Button>
            </div>
          )}
          <div className={cn("transition-opacity duration-200", isLoading && "pointer-events-none opacity-50")}>
          <Tabs defaultValue="status">
            <TabsList>
              <TabsTrigger value="status">주문 상태별 현황</TabsTrigger>
              <TabsTrigger value="keep">킵(Keep) 목록</TabsTrigger>
            </TabsList>
            <TabsContent value="status" className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {DASHBOARD_BUCKETS
                  // 스펙 D10: '부분 입금'은 count>0일 때만 노출
                  .filter((status) => status !== '부분 입금' || summary.statuses[status].count > 0)
                  .map((status) => renderStatusColumn(status, summary.statuses[status]))}
              </div>
            </TabsContent>
            <TabsContent value="keep" className="pt-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {/* 조회 기간 토글과 같은 문법(gray-100 트랙 + 흰 필) */}
                  <div role="group" aria-label="킵 목록 보기 선택" className="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-100 p-0.5">
                    {KEEP_MODE_OPTIONS.map(({ value, label }) => {
                      const isActive = value === keepMode;
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => { if (!isActive) setKeepMode(value); }}
                          className={cn(
                            "inline-flex h-7 min-w-[3.5rem] items-center justify-center rounded-md px-2.5",
                            "text-xs font-medium transition-colors duration-150",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2",
                            isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900",
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {/* 탭 이름만으로는 "전부"로 읽히므로 조회 범위를 명시 (엑셀 첫 행과 같은 문구) */}
                  <span className="text-xs text-gray-500">최근 {loadedDays ?? days}일 주문 · 향후 30일 예약 포함</span>
                </div>
                {/* 발견 수단이므로 두 보기 모두에서 같은 이름으로 노출 (스펙 §3.2).
                    보조 문구로 시트 구성을 밝혀 왼쪽 '고객별' 토글과 혼동되지 않게 한다 */}
                <div className="flex flex-col items-end gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    aria-label="상품별·고객별 시트 2장 엑셀 다운로드"
                    disabled={summary.keepItems.length === 0}
                    onClick={handleKeepExcelDownload}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    엑셀 다운로드
                  </Button>
                  <span className="text-[11px] text-gray-400">상품별·고객별 시트 2장</span>
                </div>
              </div>
              {summary.keepItems.length === 0 ? (
                <p className="text-sm text-gray-500">킵(Keep) 상태인 상품이 없습니다.</p>
              ) : keepMode === 'customer' ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>닉네임</TableHead>
                      <TableHead>상품 목록</TableHead>
                      <TableHead>총 수량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keepListView.customers.map((customer) => (
                      <TableRow key={customer.nickname}>
                        <TableCell className="font-medium">{customer.nickname}</TableCell>
                        <TableCell>
                          {customer.items.map((item) => (
                            <span key={item.name} className="mr-2 text-xs">{item.name}({item.quantity}개)</span>
                          ))}
                        </TableCell>
                        <TableCell>{customer.totalQuantity}개</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <Button variant="ghost" onClick={() => requestSort('name')}>
                          상품명
                          <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button variant="ghost" onClick={() => requestSort('totalQuantity')}>
                          총 수량
                          <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                      </TableHead>
                      <TableHead>고객별 수량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedKeepItems.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>{item.totalQuantity}개</TableCell>
                        <TableCell>
                          {Object.entries(item.nicknames).map(([nickname, quantity]) => (
                            <span key={nickname} className="mr-2 text-xs">{nickname}({quantity}개)</span>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
          </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
